/**
 * workspace.js — client for the per-user workspace surface (FE Integration PR ④).
 *
 * Pure data/transport — no React, no DOM. Unit-testable in Node.
 *
 * The workspace is the server-side home for a signed-in user's session: their
 * remembered planning defaults (`presets`), an opaque autosaved UI blob
 * (`ui_state`), a quick-load list of recent cartons, and — from the NEXT PR —
 * their in-flight durable jobs. One hydration call restores it all on sign-in.
 *
 * Endpoints (all bearer, via authFetch):
 *   GET  /workspace            hydration bundle { presets, ui_state, recent_cartons, active_jobs }
 *   GET  /workspace/presets    {} or the saved preset dict
 *   PUT  /workspace/presets    replace the WHOLE preset dict → the stored dict
 *   PUT  /workspace/ui-state   body { ui_state } → { saved, size_bytes }
 *
 * Contract notes wired in here so callers don't have to remember them:
 *   §2  presets are TYPED + all-optional; unknown keys → 422. sanitizePresets()
 *       drops anything outside the allow-list so freeform data can't 422 us.
 *   §2  PUT replaces the whole object — always send the complete set. Callers
 *       build the full dict from live form state; we don't diff.
 *   §3  ui_state is opaque, last-write-wins, 256 KB cap (over → 413
 *       ui_state_too_large). We check the size client-side and refuse early with
 *       the same code, so a debounced autosave never fires a doomed request.
 */

import { parseErrorDetail } from './exportVector';
import { authFetch } from './auth';

/** Thrown for any non-2xx /workspace response (or a client-side precheck). */
export class WorkspaceApiError extends Error {
  constructor(message, status, code, body) {
    super(message);
    this.name = 'WorkspaceApiError';
    this.status = status;     // 400 | 401 | 403 | 404 | 413 | 422 | 5xx | 0 (client-side)
    this.code = code || null; // e.g. 'ui_state_too_large'
    this.body = body || null;
  }
}

async function readError(resp) {
  let body = null;
  try { body = await resp.json(); } catch { /* non-JSON */ }
  const code = body?.detail?.code || body?.code || null;
  const msg =
    (typeof body?.detail === 'string' && body.detail) ||
    body?.detail?.message ||
    body?.message ||
    parseErrorDetail(body) ||
    `Request failed (${resp.status})`;
  return new WorkspaceApiError(msg, resp.status, code, body);
}

/* ------------------------------------------------------------------ */
/* Preset shape (§2)                                                    */
/* ------------------------------------------------------------------ */

/** The ONLY keys the backend accepts on presets. Anything else → 422. */
export const PRESET_KEYS = [
  'sheet_w_mm', 'sheet_h_mm',
  'margin_top_mm', 'margin_right_mm', 'margin_bottom_mm', 'margin_left_mm',
  'kerf_mm',
  // ⑭h / rev 8 §§1–2: `allowed_rotations` and `time_limit` were removed from
  // this allow-list. Both are engine policy now — refused from a client with
  // 400 EnginePolicyNotAccepted — so remembering them as user defaults would
  // restore a preference that can no longer take effect. Old presets that
  // still carry them are filtered out on read, which is what this allow-list
  // is for.
];

/** Which preset keys are plain non-negative millimetre numbers. */
const PRESET_MM_KEYS = [
  'sheet_w_mm', 'sheet_h_mm',
  'margin_top_mm', 'margin_right_mm', 'margin_bottom_mm', 'margin_left_mm',
  'kerf_mm',
];


/** 256 KB — the ui_state cap (§3). Over this → 413 ui_state_too_large. */
export const UI_STATE_MAX_BYTES = 256 * 1024;

/** Byte length of an object once JSON-serialised (UTF-8), for the ui_state cap. */
export function jsonByteSize(obj) {
  const s = JSON.stringify(obj ?? {});
  // TextEncoder is present in browsers and Node ≥ 11; fall back to a rough
  // char-count only if it's somehow missing (never on our targets).
  if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(s).length;
  return s.length; // eslint-disable-line
}

/**
 * Coerce/clamp an arbitrary object into a valid preset dict:
 *   · keep ONLY the allow-listed keys (freeform → dropped, never sent → no 422),
 *   · mm fields → finite numbers ≥ 0 (bad/negative → dropped),
 *   · allowed_rotations → a de-duped, sorted subset of {0,90,180,270}
 *     (empty or all-invalid → dropped rather than sent as []),
 *   · time_limit → a finite number clamped to [0, 3600].
 * All fields are optional: a key only appears in the output if it had a usable
 * value. Pure — safe to call on hydration input and before every PUT.
 */
export function sanitizePresets(input) {
  const out = {};
  if (!input || typeof input !== 'object') return out;

  for (const k of PRESET_MM_KEYS) {
    const n = Number(input[k]);
    if (Number.isFinite(n) && n >= 0) out[k] = n;
  }

  // ⑭h / rev 8: the `allowed_rotations` and `time_limit` branches were removed
  // with their keys. Both are engine policy now and a client cannot set them,
  // so a saved preset carrying either is dropped here rather than restored
  // into a control that no longer exists.

  return out;
}

/**
 * Split the carton bench into what CAN and CANNOT survive a session.
 *
 * Only library-backed cartons are restorable: a locally-dropped PDF exists as a
 * File object in the tab that dropped it and the server never received the
 * bytes. Returning the local count separately lets the UI say so honestly
 * instead of silently coming back with a shorter list.
 *
 * @returns {{ refs: Array<{libraryId:string,count:number}>, localOnly:number }}
 */
export function splitCartonRefs(vecFiles) {
  const list = Array.isArray(vecFiles) ? vecFiles : [];
  const refs = [];
  let localOnly = 0;
  for (const c of list) {
    if (c && c.libraryId) {
      const ref = { libraryId: c.libraryId, count: Math.max(1, Number(c.count) || 1) };
      // PR ⑬b (ui_state v6): the prepress tag is per-request on the export path,
      // so the bench must remember it. Added ONLY when set, so an untagged
      // carton's ref stays byte-identical to v5 — the blob doesn't grow and the
      // 256 KB cap is unaffected for every job that isn't using prepress.
      if (typeof c.prepress === 'string' && c.prepress) ref.prepress = c.prepress;
      // ⑭e (ui_state v7): ⑭d shipped `required` as session-only state, so a
      // refresh silently dropped the firm mark. Same argument as the tag — it
      // is per-request on the export path, so the bench has to remember it.
      if (c.required === true) ref.required = true;
      // ⑭e: per-carton prepress config, same additive discipline. A carton
      // with no overrides is byte-identical to a v6 ref.
      const cfg = c.prepressConfig;
      if (cfg && typeof cfg === 'object' && !Array.isArray(cfg) && Object.keys(cfg).length) {
        ref.prepress_config = cfg;
      }
      // ⑭l (ui_state v8): the carton TYPE — which die gets cut. Same argument
      // as the tag and the firm mark, and the same one ⑭d had to learn: a
      // control the bench does not remember is silently dropped on refresh, and
      // an unset type means BL, so a forgotten STE comes back as a BL and is
      // refused with 400 CartonTypeTopologyMismatch. Added ONLY when set, so a
      // carton with no type is byte-identical to a v7 ref.
      if (typeof c.cartonType === 'string' && c.cartonType) ref.carton_type = c.cartonType;
      // 1.11 (ui_state v9): the carton's SPACING — the clear space it needs
      // around it on the sheet. Per-request on the export path like the rest,
      // so the bench remembers it; added ONLY when above 0, so a carton at the
      // default is byte-identical to a v8 ref.
      const spacing = Number(c.spacingMm);
      if (Number.isFinite(spacing) && spacing > 0) ref.spacing_mm = spacing;
      refs.push(ref);
    } else localOnly += 1;
  }
  return { refs, localOnly };
}

/**
 * Normalise the contract's job `kind` into the two loops this app runs.
 *
 * v2.0 doesn't enumerate the kind values, so this is a substring match rather
 * than an equality table. Single definition, shared by the `active_jobs`
 * reattach path and the `recent_jobs` recovery path so they cannot disagree
 * about what a job is.
 *
 * @returns {'plan'|'layout'|'unknown'}
 */
export function normalizeJobKind(kind) {
  const k = String(kind || '').toLowerCase();
  if (k.includes('plan')) return 'plan';
  if (k.includes('export') || k.includes('vector') || k.includes('layout')) return 'layout';
  return 'unknown';
}

/**
 * Pick which terminal jobs to restore from hydration's `recent_jobs`.
 *
 * Backend reply #3 §1: `recent_jobs` carries the last 5 terminal jobs PER KIND
 * per user, newest first, with every terminal status — and it is disjoint from
 * `active_jobs` by construction, so no exclusion pass is needed here.
 *
 * We restore the most recent **succeeded** job of each kind. Note this may skip
 * over a newer *failed* job of the same kind, which is deliberate: when a run
 * fails, the app leaves the previous successful result on screen, so restoring
 * the last success is what actually reproduces the user's last view.
 *
 * Order is not assumed. The contract says newest-first, but since this decides
 * which layout a prepress operator sees, entries are sorted by `finished_at`
 * defensively; unparseable timestamps fall back to the given array order.
 *
 * @returns {{ layout: Object|null, plan: Object|null }}
 */
export function pickRecoverableJobs(recentJobs) {
  const list = Array.isArray(recentJobs) ? recentJobs : [];

  const ts = (j) => {
    const t = Date.parse(j?.finished_at ?? '');
    return Number.isFinite(t) ? t : null;
  };
  // Stable sort, newest first; entries without a usable timestamp keep their
  // relative position rather than being shuffled to an arbitrary end.
  const ordered = list
    .map((j, i) => ({ j, i }))
    .sort((a, b) => {
      const ta = ts(a.j); const tb = ts(b.j);
      if (ta == null && tb == null) return a.i - b.i;
      if (ta == null) return 1;
      if (tb == null) return -1;
      return tb - ta || a.i - b.i;
    })
    .map((x) => x.j);

  const out = { layout: null, plan: null };
  for (const job of ordered) {
    if (!job || job.status !== 'succeeded' || !job.job_id) continue;
    const kind = normalizeJobKind(job.kind);
    if (kind === 'unknown') continue;      // can't know which UI to restore
    if (!out[kind]) out[kind] = job;
    if (out.layout && out.plan) break;
  }
  return out;
}

/* ------------------------------------------------------------------ */
/* Calls                                                                */
/* ------------------------------------------------------------------ */

/**
 * Hydration bundle — call ONCE right after sign-in (§1). All four keys are
 * always present (empty {}/[] for a brand-new user), but we normalise defensively
 * so callers can treat them as guaranteed.
 *
 * @returns {Promise<{ presets: Object, ui_state: Object, recent_cartons: Array, active_jobs: Array }>}
 * @throws {WorkspaceApiError} — 404 if the backend predates PR ④ (caller degrades).
 */
export async function getWorkspace({ apiBase, signal } = {}) {
  const resp = await authFetch(`${apiBase}/workspace`, { signal });
  if (!resp.ok) throw await readError(resp);
  const data = await resp.json();
  return {
    presets: data?.presets && typeof data.presets === 'object' ? data.presets : {},
    ui_state: data?.ui_state && typeof data.ui_state === 'object' ? data.ui_state : {},
    recent_cartons: Array.isArray(data?.recent_cartons) ? data.recent_cartons : [],
    active_jobs: Array.isArray(data?.active_jobs) ? data.active_jobs : [],
    // Backend reply #3 §1: terminal jobs, last 5 per kind, newest first.
    // Absent on a backend that predates it — normalised to [] so recovery
    // simply finds nothing rather than throwing.
    recent_jobs: Array.isArray(data?.recent_jobs) ? data.recent_jobs : [],
  };
}

/** Read the saved presets on their own (§2). {} for a new user. */
export async function getPresets({ apiBase, signal } = {}) {
  const resp = await authFetch(`${apiBase}/workspace/presets`, { signal });
  if (!resp.ok) throw await readError(resp);
  const data = await resp.json();
  return data && typeof data === 'object' ? data : {};
}

/**
 * Replace the WHOLE preset dict (§2 — this is a PUT, not a PATCH). `presets` is
 * sanitised to the allow-list first, so unknown keys can never round-trip into a
 * 422. Returns the stored dict.
 *
 * @throws {WorkspaceApiError} — 422 only if the sanitised values are themselves
 *         out of range (shouldn't happen given sanitizePresets clamps).
 */
export async function putPresets({ apiBase, presets, signal } = {}) {
  const body = sanitizePresets(presets);
  const resp = await authFetch(`${apiBase}/workspace/presets`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!resp.ok) throw await readError(resp);
  return resp.json();
}

/**
 * Autosave the opaque UI blob (§3). We wrap it as { ui_state } per the contract
 * and refuse client-side (same code the server would use) if it exceeds the
 * 256 KB cap, so a doomed request never leaves the browser.
 *
 * `keepalive` is for the unload flush: a normal fetch is cancelled when the page
 * goes away, so a refresh inside the autosave debounce would lose the last edit.
 * `keepalive: true` lets the request outlive the document. Browsers cap keepalive
 * bodies at ~64 KB — well above our real payload (~2 KB) but below the ui_state
 * limit, so an oversized blob skips the flush rather than failing silently; the
 * ordinary debounced save remains the path for it.
 *
 * @param {Object} uiState  any JSON-serialisable object (last-write-wins).
 * @returns {Promise<{ saved: boolean, size_bytes: number }>}
 * @throws {WorkspaceApiError} — status 413 code 'ui_state_too_large' (client or server).
 */
export const KEEPALIVE_MAX_BYTES = 60 * 1024;   // conservative margin under the ~64 KB cap

export async function putUiState({ apiBase, uiState, keepalive = false, signal } = {}) {
  const payload = { ui_state: uiState ?? {} };
  const size = jsonByteSize(uiState ?? {});
  if (size > UI_STATE_MAX_BYTES) {
    throw new WorkspaceApiError(
      `UI state is ${size} bytes — over the ${UI_STATE_MAX_BYTES}-byte limit.`,
      413, 'ui_state_too_large', null,
    );
  }
  if (keepalive && size > KEEPALIVE_MAX_BYTES) {
    // Too big to survive unload; let the normal debounced save handle it.
    return { saved: false, size_bytes: size };
  }
  const resp = await authFetch(`${apiBase}/workspace/ui-state`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    keepalive: keepalive || undefined,
    signal,
  });
  if (!resp.ok) throw await readError(resp);
  return resp.json();
}
